import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Globe, MapPin, Linkedin, Instagram, Facebook, Youtube, ArrowUpRight } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { useSite } from '@/context/SiteContext';

const NAV = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Services', to: '/services' },
  { label: 'Projects', to: '/projects' },
  { label: 'Service Locations', to: '/locations' },
  { label: 'Clients', to: '/clients' },
  { label: 'Equipment', to: '/equipment' },
  { label: 'Contact', to: '/contact' },
];

export const Footer = () => {
  const { site } = useSite();
  if (!site) return null;
  const year = new Date().getFullYear();

  return (
    <footer data-testid='site-footer' className='relative bg-[#20272A] text-[#F6F5F1]'>
      <div className='grain absolute inset-0' />
      <div className='relative container-x py-16 lg:py-20'>
        <div className='grid gap-12 lg:grid-cols-12'>
          <div className='lg:col-span-4'>
            <BrandLogo height={46} />
            <p className='mt-6 max-w-sm text-sm leading-relaxed text-[#F6F5F1]/60'>{site.description}</p>
            <div className='mt-6 flex items-center gap-3'>
              {[
                { icon: Linkedin, href: site.social?.linkedin },
                { icon: Instagram, href: site.social?.instagram },
                { icon: Facebook, href: site.social?.facebook },
                { icon: Youtube, href: site.social?.youtube },
              ].map(({ icon: I, href }, idx) => (
                <a
                  key={idx}
                  href={href || '#'}
                  onClick={(e) => !href && e.preventDefault()}
                  aria-label='Social'
                  className='inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-[#F6F5F1]/70 hover:text-[#B88A3B] hover:border-[#B88A3B]/60 transition-colors'
                >
                  <I size={16} />
                </a>
              ))}
            </div>
          </div>

          <div className='lg:col-span-3'>
            <h4 className='eyebrow text-[#B88A3B]'>Explore</h4>
            <ul className='mt-5 grid grid-cols-2 gap-x-6 gap-y-3'>
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className='text-sm text-[#F6F5F1]/70 hover:text-[#F6F5F1] transition-colors'>
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className='lg:col-span-5'>
            <h4 className='eyebrow text-[#B88A3B]'>Get in touch</h4>
            <div className='mt-5 grid gap-6 sm:grid-cols-2'>
              {(site.offices || []).map((o) => (
                <div key={o.label + o.city}>
                  <p className='text-xs uppercase tracking-wider text-[#F6F5F1]/40'>{o.label} — {o.city}</p>
                  <p className='mt-2 flex items-start gap-2 text-sm text-[#F6F5F1]/70'>
                    <MapPin size={16} className='mt-0.5 shrink-0 text-[#B88A3B]' /> {o.address}
                  </p>
                </div>
              ))}
            </div>
            <div className='mt-6 space-y-2 text-sm'>
              {site.phones?.map((p) => (
                <a key={p} href={`tel:${p.replace(/\s/g, '')}`} className='mr-4 inline-flex items-center gap-2 text-[#F6F5F1]/70 hover:text-[#B88A3B]'>
                  <Phone size={15} /> {p}
                </a>
              ))}
              <div>
                <a href={`mailto:${site.email}`} className='inline-flex items-center gap-2 text-[#F6F5F1]/70 hover:text-[#B88A3B]'>
                  <Mail size={15} /> {site.email}
                </a>
              </div>
              <div>
                <a href={`https://${site.website}`} target='_blank' rel='noreferrer' className='inline-flex items-center gap-2 text-[#F6F5F1]/70 hover:text-[#B88A3B]'>
                  <Globe size={15} /> {site.website}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className='mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center'>
          <p className='text-xs text-[#F6F5F1]/45'>© {year} {site.companyName}. All rights reserved.</p>
          <div className='flex items-center gap-6 text-xs text-[#F6F5F1]/45'>
            <Link to='/contact' className='hover:text-[#F6F5F1]'>Privacy Policy</Link>
            <Link to='/contact' className='hover:text-[#F6F5F1]'>Terms</Link>
            <span className='hidden sm:inline'>Operating as {site.operatingName}</span>
          </div>
        </div>

        <div className='mt-6 border-t border-white/5 pt-5 text-center' data-testid='footer-credit'>
          <span className='text-[11px] tracking-wide text-[#F6F5F1]/35'>
            Branding, Web Designed &amp; Developed by{' '}
            <HoverCard openDelay={80} closeDelay={150}>
              <HoverCardTrigger asChild>
                <span
                  tabIndex={0}
                  role='button'
                  aria-label='rna/genesis — visit website'
                  data-testid='footer-credit-rnagenesis'
                  data-cursor='VIEW'
                  className='cursor-pointer font-medium text-[#F6F5F1]/60 underline decoration-dotted decoration-[#B88A3B]/40 underline-offset-4 transition-colors duration-200 hover:text-[#B88A3B] focus-visible:text-[#B88A3B] focus-visible:outline-none'
                >
                  rna/genesis
                </span>
              </HoverCardTrigger>
              <HoverCardContent
                align='center'
                sideOffset={10}
                className='w-auto min-w-[172px] border-white/10 bg-[#20272A]/95 p-1.5 text-left shadow-[0_16px_48px_rgba(0,0,0,0.55)] backdrop-blur-md'
              >
                <div className='flex flex-col'>
                  <a
                    href='https://rnagenesis.ai'
                    target='_blank'
                    rel='noreferrer'
                    data-testid='footer-credit-link-ai'
                    className='flex items-center justify-between gap-6 rounded-md px-3 py-2 text-sm text-[#F6F5F1]/80 transition-colors duration-200 hover:bg-white/5 hover:text-[#B88A3B]'
                  >
                    rnagenesis.ai <ArrowUpRight size={14} className='shrink-0' />
                  </a>
                  <a
                    href='https://rnagenesis.in'
                    target='_blank'
                    rel='noreferrer'
                    data-testid='footer-credit-link-in'
                    className='flex items-center justify-between gap-6 rounded-md px-3 py-2 text-sm text-[#F6F5F1]/80 transition-colors duration-200 hover:bg-white/5 hover:text-[#B88A3B]'
                  >
                    rnagenesis.in <ArrowUpRight size={14} className='shrink-0' />
                  </a>
                </div>
              </HoverCardContent>
            </HoverCard>
          </span>
        </div>
      </div>
    </footer>
  );
};
